import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Switch, Redirect } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { MotionConfig } from 'framer-motion';
import { supabase } from './supabaseClient';
import CookieConsentModal from './components/CookieConsentModal';
import MaintenanceWrapper from './components/MaintenanceWrapper';
import VersionUpdateBanner from './components/shared/VersionUpdateBanner';
import { OrderingProvider } from './hooks/useOrderingEnabled';
import { logger } from './utils/logger';

// Import components
import Login from './components/Login';
import ResetPassword from './components/ResetPassword';
import SetPassword from './components/SetPassword';
import AuthCallback from './components/AuthCallback';
import ErrorBoundary from './components/ErrorBoundary';
import UserSettings from './components/UserSettings';
import Account from './components/Account';
import ProductDocuments from './components/ProductDocuments';
import StauffCatalogues from './components/StauffCatalogues';
import StauffCatalogueDetail from './components/StauffCatalogueDetail';
import NotFound from './components/NotFound';
import Scanner from './components/Scanner';
import AdminLayout from './components/admin/AdminLayout';
import AdminDashboard from './components/admin/AdminDashboard';
import CompanyAdminDashboard from './components/admin/CompanyAdminDashboard';
import CompanyOrders from './components/admin/CompanyOrders';
import AdminOrderApproval from './components/admin/AdminOrderApproval';
import UserManagement from './components/admin/UserManagement';
import OrderManagement from './components/admin/OrderManagement';
import ContentManagement from './components/admin/ContentManagement';
import SearchAnalyticsDashboard from './components/admin/SearchAnalyticsDashboard';
import ProductUpload from './components/admin/ProductUpload';
import PriceScheduleUpload from './components/admin/PriceScheduleUpload';
import AdminCustomerNotifications from './components/admin/AdminCustomerNotifications';
import DeliveryAddressManagement from './components/admin/DeliveryAddressManagement';
import BackorderManagement from './components/admin/BackorderManagement';
import ChangelogManagement from './components/admin/ChangelogManagement';
import SystemSettings from './components/admin/SystemSettings';
import CeleryQueueMonitor from './components/admin/CeleryQueueMonitor';
import AdminOnlyRoute from './components/AdminOnlyRoute';
import AdminOrCompanyAdminRoute from './components/AdminOrCompanyAdminRoute';
import StaffOnlyRoute from './components/StaffOnlyRoute';
import StaffLayout from './components/staff/StaffLayout';
import StaffDashboard from './components/staff/StaffDashboard';
import StaffOrderManagement from './components/staff/StaffOrderManagement';
import OrderApproval from './components/staff/OrderApproval';
import InquiryManagement from './components/staff/InquiryManagement';
import InquiryReview from './components/staff/InquiryReview';

// Import new user layout components
import UserLayout from './components/user/UserLayout';
import UserRecentOrders from './components/user/UserRecentOrders';
import UserPartEnquiry from './components/user/UserPartEnquiry';
import UserCart from './components/user/UserCart';
import UserBulkUpload from './components/user/UserBulkUpload';
import UserBulkQuery from './components/user/UserBulkQuery';
import UserPartNumberManagement from './components/user/UserPartNumberManagement';
import UserCheckout from './components/user/UserCheckout';
import UserDashboardNew from './components/user/UserDashboardNew';
import UserSlspPromo from './components/user/UserSlspPromo';
import UserContact from './components/user/UserContact';
import Developer from './components/user/Developer';
import InquiryLists from './components/user/InquiryLists';
import InquiryListDetail from './components/user/InquiryListDetail';
import UserProductDetail from './components/user/UserProductDetail';
import SealLookup from './components/user/SealLookup';

// Admin redirect component to route based on user type
const AdminRedirect = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data } = await supabase
          .from('accounts')
          .select('is_admin, is_company_admin')
          .eq('id', user.id)
          .single();
        setUser(data);
      }
      setLoading(false);
    };
    checkUser();
  }, []);

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand"></div>
    </div>;
  }

  // If user is BOTH admin and company admin, show company dashboard
  // This allows company admins to have admin permissions for data access
  // while still seeing the company-specific dashboard
  if (user?.is_admin && user?.is_company_admin) {
    return (
      <AdminLayout>
        <CompanyAdminDashboard />
      </AdminLayout>
    );
  }

  // System admins (not company admins) go to main admin dashboard
  if (user?.is_admin) {
    return (
      <AdminLayout>
        <AdminDashboard />
      </AdminLayout>
    );
  }

  // Company admins (not system admins) go to company dashboard
  if (user?.is_company_admin) {
    return (
      <AdminLayout>
        <CompanyAdminDashboard />
      </AdminLayout>
    );
  }

  return <Redirect to="/user/dashboard" />;
};

function App() {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCookieConsent, setShowCookieConsent] = useState(false);
  const [cookieConsentChecked, setCookieConsentChecked] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setLoading(false);
      
      // Check cookie consent status when session is established
      if (session) {
        checkCookieConsent(session.user.id);
        initializeLogger(session.user.id);
      }
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);

      // Copies Supabase Auth's own sign-in time, so repeat SIGNED_IN events are harmless
      if (event === 'SIGNED_IN') {
        supabase.rpc('sync_last_login').then(({ error }) => {
          if (error) console.error('Error syncing last login:', error);
        });
      }

      // Check cookie consent when user logs in
      if (session) {
        checkCookieConsent(session.user.id);
        initializeLogger(session.user.id);
      } else {
        // Reset consent modal state when user logs out
        setShowCookieConsent(false);
        setCookieConsentChecked(false);
        // Reset logger when user logs out
        logger.setUserRole(false, false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const initializeLogger = async (userId) => {
    try {
      const { data: account, error } = await supabase
        .from('accounts')
        .select('is_admin, is_staff')
        .eq('id', userId)
        .single();

      if (error) throw error;

      if (account) {
        logger.setUserRole(account.is_admin || false, account.is_staff || false);
        logger.debug('Logger initialized for user:', {
          isAdmin: account.is_admin,
          isStaff: account.is_staff
        });
      }
    } catch (error) {
      logger.error('Error initializing logger:', error);
    }
  };

  const checkCookieConsent = async (userId) => {
    try {
      const { data, error } = await supabase
        .from('accounts')
        .select('cookie_consent')
        .eq('id', userId)
        .single();

      if (error) throw error;

      // Show modal if cookie_consent is null (not yet decided)
      setShowCookieConsent(data.cookie_consent === null);
      setCookieConsentChecked(true);
    } catch (error) {
      console.error('Error checking cookie consent:', error);
      setCookieConsentChecked(true);
    }
  };

  const handleCookieConsent = async (accepted) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { error } = await supabase
        .from('accounts')
        .update({
          cookie_consent: accepted,
          cookie_consent_date: new Date().toISOString()
        })
        .eq('id', user.id);

      if (error) throw error;

      setShowCookieConsent(false);
      
      // Optionally show a notification
      console.log(accepted ? 'Cookies accepted' : 'Optional cookies denied');
    } catch (error) {
      console.error('Error updating cookie consent:', error);
    }
  };

  if (loading || (session && !cookieConsentChecked)) {
    return <div>Loading...</div>;
  }

  return (
    <ErrorBoundary>
      {/* Honour the OS "reduce motion" setting for every framer-motion animation */}
      <MotionConfig reducedMotion="user">
      <CookieConsentModal
        isOpen={showCookieConsent}
        onAccept={() => handleCookieConsent(true)}
        onDeny={() => handleCookieConsent(false)}
      />
      <OrderingProvider session={session}>
      <Router>
        <MaintenanceWrapper>
          <div className="App">
            <VersionUpdateBanner />
            <ToastContainer
              position="top-right"
              autoClose={3000}
              hideProgressBar={false}
              newestOnTop
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
              theme="dark"
            />
            <Switch>
          <Route exact path="/">
            {session ? <Redirect to="/user/dashboard" /> : <Login />}
          </Route>
          <Route exact path="/login">
            {session ? <Redirect to="/user/dashboard" /> : <Login />}
          </Route>
          <Route exact path="/auth/callback">
            <AuthCallback />
          </Route>
          <Route exact path="/reset-password">
            <ResetPassword />
          </Route>
          <Route exact path="/set-password">
            {!session ? <Redirect to="/" /> : <SetPassword />}
          </Route>
          {/* Old dashboard URLs (bookmarks) */}
          <Route exact path={['/dashboard', '/dashboard-legacy', '/user/dashboard-old']}>
            {!session ? <Redirect to="/" /> : <Redirect to="/user/dashboard" />}
          </Route>
          {/* Old customer-area URLs (pre-/user layout) -> current pages, keeping any query string */}
          <Route
            path={['/part-enquiry', '/cart', '/recent-orders', '/checkout', '/bulk-upload', '/slsp-promo', '/part-number-management']}
            render={({ location }) => (
              <Redirect to={{ pathname: `/user${location.pathname}`, search: location.search }} />
            )}
          />
          <Route path="/user-settings">
            {!session ? <Redirect to="/" /> : <UserSettings />}
          </Route>
          <Route path="/account">
            {!session ? <Redirect to="/" /> : <Account />}
          </Route>
          <Route path="/product-documents">
            {!session ? <Redirect to="/" /> : <ProductDocuments />}
          </Route>
          <Route path="/stauff-catalogues">
            {!session ? <Redirect to="/" /> : <Redirect to="/user/product-documents/stauff-catalogues" />}
          </Route>
          <Route path="/stauff-clamps">
            {!session ? <Redirect to="/" /> : <Redirect to="/user/product-documents/stauff-catalogues/clamps" />}
          </Route>
          <Route exact path="/scanner">
            {!session ? <Redirect to="/" /> : <Scanner />}
          </Route>
          <Route exact path="/staff-area">
            {!session ? <Redirect to="/" /> : 
              <StaffOnlyRoute>
                <StaffLayout>
                  <StaffDashboard />
                </StaffLayout>
              </StaffOnlyRoute>
            }
          </Route>
          <Route path="/staff-area/orders">
            {!session ? <Redirect to="/" /> : 
              <StaffOnlyRoute>
                <StaffLayout>
                  <StaffOrderManagement />
                </StaffLayout>
              </StaffOnlyRoute>
            }
          </Route>
          <Route path="/staff-area/approvals">
            {!session ? <Redirect to="/" /> : 
              <StaffOnlyRoute>
                <StaffLayout>
                  <OrderApproval />
                </StaffLayout>
              </StaffOnlyRoute>
            }
          </Route>
          <Route path="/staff-area/search-analytics">
            {!session ? <Redirect to="/" /> : 
              <StaffOnlyRoute>
                <StaffLayout>
                  <SearchAnalyticsDashboard />
                </StaffLayout>
              </StaffOnlyRoute>
            }
          </Route>
          <Route exact path="/staff-area/inquiries">
            {!session ? <Redirect to="/" /> : 
              <StaffOnlyRoute>
                <StaffLayout>
                  <InquiryManagement />
                </StaffLayout>
              </StaffOnlyRoute>
            }
          </Route>
          <Route path="/staff-area/inquiry/:id">
            {!session ? <Redirect to="/" /> : 
              <StaffOnlyRoute>
                <StaffLayout>
                  <InquiryReview />
                </StaffLayout>
              </StaffOnlyRoute>
            }
          </Route>
          {/* Old /staff URLs — still used in approve/reject/view-order email links.
              /staff -> /staff-area, /staff/orders?highlight=… -> /staff-area/orders?highlight=… */}
          <Route
            path="/staff"
            render={({ location }) => (
              <Redirect
                to={{
                  pathname: location.pathname.replace(/^\/staff/, '/staff-area'),
                  search: location.search,
                }}
              />
            )}
          />
          <Route exact path="/admin">
            {!session ? <Redirect to="/" /> :
              <AdminOrCompanyAdminRoute>
                <AdminRedirect />
              </AdminOrCompanyAdminRoute>
            }
          </Route>
          <Route path="/admin/company-dashboard">
            {!session ? <Redirect to="/" /> :
              <AdminOrCompanyAdminRoute>
                <AdminLayout>
                  <CompanyAdminDashboard />
                </AdminLayout>
              </AdminOrCompanyAdminRoute>
            }
          </Route>
          <Route path="/admin/company-orders">
            {!session ? <Redirect to="/" /> :
              <AdminOrCompanyAdminRoute>
                <AdminLayout>
                  <CompanyOrders />
                </AdminLayout>
              </AdminOrCompanyAdminRoute>
            }
          </Route>
          <Route path="/admin/users">
            {!session ? <Redirect to="/" /> :
              <AdminOrCompanyAdminRoute>
                <AdminLayout>
                  <UserManagement />
                </AdminLayout>
              </AdminOrCompanyAdminRoute>
            }
          </Route>
          <Route path="/admin/orders">
            {!session ? <Redirect to="/" /> :
              <AdminOnlyRoute>
                <AdminLayout>
                  <OrderManagement />
                </AdminLayout>
              </AdminOnlyRoute>
            }
          </Route>
          <Route path="/admin/approvals">
            {!session ? <Redirect to="/" /> :
              <AdminOnlyRoute>
                <AdminLayout>
                  <AdminOrderApproval />
                </AdminLayout>
              </AdminOnlyRoute>
            }
          </Route>
          <Route path="/admin/notifications">
            {!session ? <Redirect to="/" /> :
              <AdminOnlyRoute>
                <AdminLayout>
                  <AdminCustomerNotifications />
                </AdminLayout>
              </AdminOnlyRoute>
            }
          </Route>
          <Route path="/admin/delivery-addresses">
            {!session ? <Redirect to="/" /> :
              <AdminOrCompanyAdminRoute>
                <AdminLayout>
                  <DeliveryAddressManagement />
                </AdminLayout>
              </AdminOrCompanyAdminRoute>
            }
          </Route>
          <Route path="/admin/content">
            {!session ? <Redirect to="/" /> :
              <AdminOnlyRoute>
                <AdminLayout>
                  <ContentManagement />
                </AdminLayout>
              </AdminOnlyRoute>
            }
          </Route>
          <Route path="/admin/products">
            {!session ? <Redirect to="/" /> :
              <AdminOnlyRoute>
                <AdminLayout>
                  <ProductUpload />
                </AdminLayout>
              </AdminOnlyRoute>
            }
          </Route>
          <Route path="/admin/price-schedules">
            {!session ? <Redirect to="/" /> :
              <AdminOnlyRoute>
                <AdminLayout>
                  <PriceScheduleUpload />
                </AdminLayout>
              </AdminOnlyRoute>
            }
          </Route>
          <Route path="/admin/search-analytics">
            {!session ? <Redirect to="/" /> :
              <AdminOnlyRoute>
                <AdminLayout>
                  <SearchAnalyticsDashboard />
                </AdminLayout>
              </AdminOnlyRoute>
            }
          </Route>
          <Route exact path="/admin/inquiries">
            {!session ? <Redirect to="/" /> :
              <AdminOnlyRoute>
                <AdminLayout>
                  <InquiryManagement context="admin" />
                </AdminLayout>
              </AdminOnlyRoute>
            }
          </Route>
          <Route path="/admin/inquiry/:id">
            {!session ? <Redirect to="/" /> :
              <AdminOnlyRoute>
                <AdminLayout>
                  <InquiryReview context="admin" />
                </AdminLayout>
              </AdminOnlyRoute>
            }
          </Route>
          <Route path="/admin/backorders">
            {!session ? <Redirect to="/" /> :
              <AdminOnlyRoute>
                <AdminLayout>
                  <BackorderManagement />
                </AdminLayout>
              </AdminOnlyRoute>
            }
          </Route>
          <Route path="/admin/changelog">
            {!session ? <Redirect to="/" /> :
              <AdminOnlyRoute>
                <AdminLayout>
                  <ChangelogManagement />
                </AdminLayout>
              </AdminOnlyRoute>
            }
          </Route>
          <Route path="/admin/system-settings">
            {!session ? <Redirect to="/" /> :
              <AdminOnlyRoute>
                <AdminLayout>
                  <SystemSettings />
                </AdminLayout>
              </AdminOnlyRoute>
            }
          </Route>
          <Route path="/admin/settings">
            {!session ? <Redirect to="/" /> :
              <AdminOnlyRoute>
                <AdminLayout>
                  <SystemSettings />
                </AdminLayout>
              </AdminOnlyRoute>
            }
          </Route>
          <Route path="/admin/queue-monitor">
            {!session ? <Redirect to="/" /> :
              <AdminOnlyRoute>
                <AdminLayout>
                  <CeleryQueueMonitor />
                </AdminLayout>
              </AdminOnlyRoute>
            }
          </Route>
          
          {/* New User Layout Routes */}
          <Route exact path="/user/dashboard">
            {!session ? <Redirect to="/" /> : <UserDashboardNew />}
          </Route>
          <Route path="/user/recent-orders">
            {!session ? <Redirect to="/" /> : <UserRecentOrders />}
          </Route>
          <Route path="/user/product/:partNumber">
            {!session ? <Redirect to="/" /> : <UserProductDetail />}
          </Route>
          <Route path="/user/part-enquiry">
            {!session ? <Redirect to="/" /> : <UserPartEnquiry />}
          </Route>
          <Route path="/user/seal-lookup">
            {!session ? <Redirect to="/" /> : <SealLookup />}
          </Route>
          <Route path="/user/cart">
            {!session ? <Redirect to="/" /> : <UserCart />}
          </Route>
          <Route path="/user/settings">
            {!session ? <Redirect to="/" /> : <UserSettings />}
          </Route>
          <Route path="/user/bulk-upload">
            {!session ? <Redirect to="/" /> : <UserBulkUpload />}
          </Route>
          <Route path="/user/bulk-query">
            {!session ? <Redirect to="/" /> : <UserBulkQuery />}
          </Route>
          <Route path="/user/part-number-management">
            {!session ? <Redirect to="/" /> : <UserPartNumberManagement />}
          </Route>
          <Route path="/user/checkout">
            {!session ? <Redirect to="/" /> : <UserCheckout />}
          </Route>
          <Route exact path="/user/product-documents">
            {!session ? <Redirect to="/" /> : <ProductDocuments />}
          </Route>
          <Route exact path="/user/product-documents/stauff-catalogues">
            {!session ? <Redirect to="/" /> : <StauffCatalogues />}
          </Route>
          <Route path="/user/product-documents/stauff-catalogues/:slug">
            {!session ? <Redirect to="/" /> : <StauffCatalogueDetail />}
          </Route>
          <Route path="/user/slsp-promo">
            {!session ? <Redirect to="/" /> : <UserSlspPromo />}
          </Route>
          <Route path="/user/contact">
            {!session ? <Redirect to="/" /> : <UserContact />}
          </Route>
          <Route path="/user/developer">
            {!session ? <Redirect to="/" /> : <Developer />}
          </Route>
          <Route path="/user/scanner">
            {!session ? <Redirect to="/" /> : <Scanner />}
          </Route>
          <Route exact path="/user/inquiry-lists">
            {!session ? <Redirect to="/" /> : <InquiryLists />}
          </Route>
          <Route path="/user/inquiry-list/:id">
            {!session ? <Redirect to="/" /> : <InquiryListDetail />}
          </Route>
          
          <Route component={NotFound} />
          </Switch>
          </div>
        </MaintenanceWrapper>
      </Router>
      </OrderingProvider>
      </MotionConfig>
    </ErrorBoundary>
  );
}

export default App;
